import { useMemo, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, Loader2, ChevronsRight } from 'lucide-react';
import { api } from '../../lib/auth';
import { toast } from 'sonner';

const MONTH_OF_WEEK = (yr, w) => {
  // ISO week → Monday → month (0-11). Used to bucket the week dropdown grid.
  try {
    const d = new Date(Date.UTC(yr, 0, 1 + (w - 1) * 7));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + (1 - dayNum));
    return d.getUTCMonth();
  } catch { return 0; }
};
const MONTHS_TR = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];

/**
 * Quick-jump week picker. Opens from the "{year} · W{n}" pill in the Planning
 * toolbar. Shows a 53-cell grid coloured by fill-rate (uses
 * /planning/yearly-summary, fetched lazily on first open and cached for the
 * lifetime of the popover instance).
 *
 * Click any week → calls onPick(year, week) and closes the popover.
 */
export function WeekPicker({ year, week, onPick, onPrevYear, onNextYear, onCopyDone }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hoverYear, setHoverYear] = useState(year);
  // Drag/drop kopyalama state'i. dragSrc → sürüklenen kaynak hafta
  // {year, week, assigned}; hoverTgt → üzerine sürüklenen hedef hafta
  // (visual highlight için). copying → backend POST aktif (UI lock).
  const [dragSrc, setDragSrc] = useState(null);
  const [hoverTgt, setHoverTgt] = useState(null);
  const [copying, setCopying] = useState(false);
  const [confirmCopy, setConfirmCopy] = useState(null); // {src, tgt}
  const [fanout, setFanout] = useState(null); // {src, count} — "Sonraki N haftaya kopyala" akışı

  // Çoklu hafta kopyalama: kaynak haftadan itibaren `count` ardışık hafta'ya
  // sırayla kopyalar. Yıl sınırını aşarsa ISO week 52/53'ten sonraki yıla
  // geçer. Hedef haftada veri varsa default `merge` (overwrite=false) — operatör
  // istediğinde modal'dan "üzerine yaz" seçeneğine geçebilir.
  const performFanout = async ({ src, count, overwrite }) => {
    if (!src || !count || count < 1) return;
    setCopying(true);
    let totalCopied = 0;
    let totalSkipped = 0;
    let failures = 0;
    const targets = [];
    let curYear = src.year;
    let curWeek = src.week;
    for (let i = 0; i < count; i += 1) {
      // Sonraki haftaya geç. ISO yıl-değişimi: W52/53 sonrası Ocak'ın 1.
      // haftasına. Backend hedef haftayı validate ediyor; geçersiz olursa
      // 400 atar — biz sadece sayaç tutuyoruz.
      curWeek += 1;
      if (curWeek > 53) { curWeek = 1; curYear += 1; }
      targets.push({ year: curYear, week: curWeek });
    }
    for (const tgt of targets) {
      try {
        const res = await api.post('/planning/copy-week', {
          source_year: src.year, source_week: src.week,
          target_year: tgt.year, target_week: tgt.week,
          overwrite: !!overwrite,
        });
        const d = res.data?.data || {};
        totalCopied += d.copied ?? 0;
        totalSkipped += d.skipped ?? 0;
      } catch {
        failures += 1;
      }
    }
    if (failures === count) {
      toast.error('Çoklu kopya başarısız oldu');
    } else if (failures > 0) {
      toast.warning(`${count - failures}/${count} hafta kopyalandı. ${totalCopied} atama, ${totalSkipped} atlandı.`);
    } else {
      toast.success(`W${src.week} → ${count} haftaya: ${totalCopied} atama kopyalandı${totalSkipped ? `, ${totalSkipped} atlandı` : ''}`);
    }
    load(hoverYear);
    if (targets.length > 0) onCopyDone?.(targets[targets.length - 1].year, targets[targets.length - 1].week);
    setCopying(false);
    setFanout(null);
  };

  const load = async (y) => {
    setLoading(true);
    try {
      const res = await api.get(`/planning/yearly-summary?year=${y}`);
      setData(res.data?.data || null);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = (next) => {
    setOpen(next);
    if (next && (!data || data.year !== hoverYear)) load(hoverYear);
  };

  // Drag-and-drop: tek bir week tile'ı diğer week tile'ın üzerine bırakıldığında
  // backend `/planning/copy-week` endpoint'i kaynak haftanın atamalarını
  // (gün-bazlı offset ile) hedefe kopyalar. Kaynak ve hedef AYNI ise no-op.
  // Hedef haftada zaten atama varsa operatöre confirm sorulur (overwrite=false
  // default — mevcut atamalar korunur, sadece çakışmayanlar eklenir).
  const performCopy = async ({ src, tgt, overwrite }) => {
    if (!src || !tgt) return;
    if (src.year === tgt.year && src.week === tgt.week) return;
    setCopying(true);
    try {
      const res = await api.post('/planning/copy-week', {
        source_year: src.year, source_week: src.week,
        target_year: tgt.year, target_week: tgt.week,
        overwrite: !!overwrite,
      });
      const d = res.data?.data || {};
      const copied = d.copied ?? 0;
      const skipped = d.skipped ?? 0;
      const lockedHits = d.locked_hits ?? 0;
      const lockedNote = lockedHits > 0 ? ` (${lockedHits} kilitli güne dahil)` : '';
      if (copied === 0 && skipped === 0) {
        toast.info('Kopyalanacak atama bulunamadı');
      } else if (skipped > 0) {
        toast.success(`W${src.week} → W${tgt.week}: ${copied} kopyalandı${lockedNote}, ${skipped} atlandı (çakışma/izin)`);
      } else {
        toast.success(`W${src.week} → W${tgt.week}: ${copied} atama kopyalandı${lockedNote}`);
      }
      // Yıllık özeti yeniden yükle ki hedef haftanın doluluğu ve sayısı güncellensin
      load(hoverYear);
      onCopyDone?.(tgt.year, tgt.week);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Kopyalama başarısız');
    } finally {
      setCopying(false);
      setConfirmCopy(null);
    }
  };

  // Drop tetikleyicisi: hedefte zaten atama varsa modal sor; yoksa direkt kopyala.
  const handleDrop = (tgt) => {
    if (!dragSrc) return;
    if (dragSrc.year === tgt.year && dragSrc.week === tgt.week) {
      setDragSrc(null);
      setHoverTgt(null);
      return;
    }
    if ((tgt.assigned || 0) > 0) {
      setConfirmCopy({ src: { ...dragSrc }, tgt: { ...tgt } });
    } else {
      performCopy({ src: dragSrc, tgt, overwrite: false });
    }
    setDragSrc(null);
    setHoverTgt(null);
  };

  const monthBuckets = useMemo(() => {
    if (!data) return [];
    const out = Array.from({ length: 12 }, (_, i) => ({ month: i, weeks: [] }));
    for (const w of data.weeks) {
      const m = MONTH_OF_WEEK(data.year, w.week);
      out[m].weeks.push(w);
    }
    return out.filter((b) => b.weeks.length > 0);
  }, [data]);

  return (
    <Popover open={open} onOpenChange={handleOpen}>
      <PopoverTrigger asChild>
        <button
          className="px-3 py-1 rounded bg-slate-100 dark:bg-slate-800 text-sm font-semibold hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors inline-flex items-center gap-1.5"
          data-testid="planning-week-picker-trigger"
          title="Tüm haftaları göster"
        >
          <CalendarIcon className="h-4 w-4 text-slate-500" />
          {year} · W{week}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[420px] p-3" align="start" data-testid="planning-week-picker-popover">
        <div className="flex items-center justify-between mb-2">
          <button
            onClick={() => { const y = hoverYear - 1; setHoverYear(y); load(y); onPrevYear?.(); }}
            className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800"
            data-testid="planning-week-picker-prev-year"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
          </button>
          <div className="text-sm font-bold tabular-nums">{hoverYear}</div>
          <button
            onClick={() => { const y = hoverYear + 1; setHoverYear(y); load(y); onNextYear?.(); }}
            className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800"
            data-testid="planning-week-picker-next-year"
          >
            <ChevronRight className="h-3.5 w-3.5" />
          </button>
        </div>
        {loading ? (
          <div className="h-32 flex items-center justify-center"><Loader2 className="h-4 w-4 animate-spin text-slate-400" /></div>
        ) : !data ? (
          <p className="text-xs text-slate-400 text-center py-4">Veri yok</p>
        ) : (
          <div className="space-y-1.5 max-h-[60vh] overflow-y-auto">
            {monthBuckets.map(({ month, weeks }) => (
              <div key={month} className="flex items-center gap-2">
                <span className="text-[10px] uppercase tracking-wide text-slate-400 w-8 shrink-0">{MONTHS_TR[month]}</span>
                <div className="flex flex-wrap gap-1 flex-1">
                  {weeks.map((w) => {
                    const isCurrent = data.year === year && w.week === week;
                    const isDragSrc = dragSrc && dragSrc.year === data.year && dragSrc.week === w.week;
                    const isHoverTgt = hoverTgt && hoverTgt.year === data.year && hoverTgt.week === w.week && !isDragSrc;
                    const filled = (w.assigned || 0) > 0;
                    return (
                      <div key={w.week} className="relative group">
                        <button
                          draggable={filled && !copying}
                          onDragStart={(e) => {
                            if (!filled) { e.preventDefault(); return; }
                            setDragSrc({ year: data.year, week: w.week, assigned: w.assigned });
                            e.dataTransfer.effectAllowed = 'copy';
                            try { e.dataTransfer.setData('text/plain', `week:${data.year}-${w.week}`); } catch { /* ignore */ }
                          }}
                          onDragEnd={() => { setDragSrc(null); setHoverTgt(null); }}
                          onDragOver={(e) => {
                            if (!dragSrc) return;
                            e.preventDefault();
                            e.dataTransfer.dropEffect = 'copy';
                            if (!isHoverTgt) setHoverTgt({ year: data.year, week: w.week });
                          }}
                          onDragLeave={() => { if (isHoverTgt) setHoverTgt(null); }}
                          onDrop={(e) => {
                            if (!dragSrc) return;
                            e.preventDefault();
                            handleDrop({ year: data.year, week: w.week, assigned: w.assigned });
                          }}
                          onClick={() => { if (!dragSrc) { onPick(data.year, w.week); setOpen(false); } }}
                          onContextMenu={(e) => {
                            if (!filled) return;
                            e.preventDefault();
                            setFanout({ src: { year: data.year, week: w.week, assigned: w.assigned } });
                          }}
                          className={`text-[11px] font-mono font-semibold w-12 h-8 rounded border transition-all ${
                            isCurrent
                              ? 'bg-emerald-500 text-white border-emerald-600 ring-2 ring-emerald-300'
                              : isHoverTgt
                                ? 'bg-blue-100 dark:bg-blue-900/40 border-blue-500 ring-2 ring-blue-400 scale-105'
                                : isDragSrc
                                  ? 'bg-amber-100 dark:bg-amber-900/40 border-amber-400 ring-2 ring-amber-300 opacity-60'
                                  : filled
                                    ? `${heatColor(w.fill_rate)} border-transparent text-slate-700 dark:text-slate-200 hover:ring-2 hover:ring-emerald-300 cursor-grab active:cursor-grabbing`
                                    : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-700'
                          }`}
                          title={
                            filled
                              ? `W${w.week} (${w.start_date} → ${w.end_date}) — ${w.assigned} atama\nSürükle → tek haftaya kopyala\nSağ tık veya köşedeki ↠ butonu → çoklu kopya`
                              : `W${w.week} (${w.start_date} → ${w.end_date}) — atama yok`
                          }
                          data-testid={`planning-week-picker-w-${w.week}`}
                        >
                          W{w.week}
                          {filled && (
                            <span className="block text-[8px] opacity-80">{w.assigned}</span>
                          )}
                        </button>
                        {/* Köşedeki "Çoklu kopya" düğmesi — dolu hücrelerde
                            hover'da görünür. Sağ tık çalışmayan ortamlar
                            (touch, bazı tarayıcılar) için garantili erişim.
                            Aktif (current) haftayı da kapsar — operatör
                            bulunduğu haftayı sonraki haftalara çoğaltabilir. */}
                        {filled && !isDragSrc && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setFanout({ src: { year: data.year, week: w.week, assigned: w.assigned } });
                            }}
                            className={`absolute -top-1.5 -right-1.5 h-4 w-4 rounded-full text-white shadow-md flex items-center justify-center transition-opacity ${isCurrent ? 'bg-amber-500 hover:bg-amber-600 opacity-100' : 'bg-emerald-600 hover:bg-emerald-700 opacity-0 group-hover:opacity-100'}`}
                            title="Çoklu kopya — sonraki N haftaya"
                            data-testid={`planning-week-fanout-btn-w-${w.week}`}
                          >
                            <ChevronsRight className="h-2.5 w-2.5" />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
        {/* Drag-drop ipucu — operatöre özelliğin var olduğunu hatırlatır */}
        {dragSrc && (
          <div className="text-[10px] text-blue-600 dark:text-blue-300 mt-2 pt-2 border-t border-blue-100 dark:border-blue-900/40 bg-blue-50/50 dark:bg-blue-900/20 px-2 py-1 rounded">
            <strong>W{dragSrc.week}</strong>'i ({dragSrc.assigned} atama) hangi haftaya kopyalamak istiyorsun? Hedef hücreye bırak.
          </div>
        )}
        {copying && (
          <div className="text-[10px] text-emerald-600 mt-2 pt-2 border-t border-emerald-100 inline-flex items-center gap-1">
            <Loader2 className="h-3 w-3 animate-spin" /> Kopyalanıyor…
          </div>
        )}
        <div className="flex items-center justify-between text-[10px] text-slate-400 mt-2 pt-2 border-t border-slate-100 dark:border-slate-700">
          <span>Boş</span>
          <div className="flex items-center gap-0.5">
            {[0, 20, 40, 60, 80, 100].map((p) => (
              <span key={p} className={`inline-block h-2.5 w-2.5 rounded-sm ${heatColor(p)}`} />
            ))}
          </div>
          <span>Dolu</span>
        </div>
      </PopoverContent>

      {/* Hedef haftada zaten atama varsa onay modal'ı. Modal Popover dışında
          render ediliyor — popover kapanırsa bile confirm'i kaybetmeyiz. */}
      {confirmCopy && (
        <div
          className="fixed inset-0 z-[100] bg-black/40 flex items-center justify-center p-4"
          onClick={() => !copying && setConfirmCopy(null)}
          data-testid="planning-week-copy-confirm-overlay"
        >
          <div
            className="bg-white dark:bg-slate-800 rounded-lg shadow-2xl p-4 max-w-sm w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-sm font-semibold mb-1">Hafta Kopyalama Onayı</h3>
            <p className="text-xs text-slate-600 dark:text-slate-300 mb-3">
              Hedef hafta <strong>W{confirmCopy.tgt.week}</strong> ({confirmCopy.tgt.assigned} mevcut atama)
              boş değil. Kaynak <strong>W{confirmCopy.src.week}</strong>'den nasıl kopyalansın?
            </p>
            <div className="space-y-2">
              <button
                onClick={() => performCopy({ ...confirmCopy, overwrite: false })}
                disabled={copying}
                className="w-full text-left text-xs px-3 py-2 rounded border border-emerald-200 hover:bg-emerald-50 dark:hover:bg-emerald-900/20"
                data-testid="planning-week-copy-merge"
              >
                <div className="font-semibold">Birleştir</div>
                <div className="text-[10px] text-slate-500">Mevcut atamalar korunur, çakışmayanlar eklenir.</div>
              </button>
              <button
                onClick={() => performCopy({ ...confirmCopy, overwrite: true })}
                disabled={copying}
                className="w-full text-left text-xs px-3 py-2 rounded border border-red-200 hover:bg-red-50 dark:hover:bg-red-900/20"
                data-testid="planning-week-copy-overwrite"
              >
                <div className="font-semibold text-red-600 dark:text-red-300">Üzerine yaz</div>
                <div className="text-[10px] text-slate-500">Hedef haftadaki tüm mevcut atamalar SİLİNİR, sonra kopyalanır.</div>
              </button>
              <button
                onClick={() => setConfirmCopy(null)}
                disabled={copying}
                className="w-full text-xs px-3 py-1.5 rounded text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700"
              >
                İptal
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Çoklu hafta kopyalama modal — sağ tık ile açılır.
          "Sonraki N haftaya kopyala" preset'leri sezonluk rotasyonlar için
          ideal: bir kez planlama yap, ay/çeyrek boyunca tekrar et. */}
      {fanout && (() => {
        const presets = [1, 2, 4, 8, 12];
        // Hedef hafta numaralarını insanca formatla: W19, W19-W20, W19-W22 vs.
        const rangeLabel = (n) => {
          let yr = fanout.src.year, wk = fanout.src.week;
          const last = (() => {
            let y = yr, w = wk;
            for (let i = 0; i < n; i += 1) {
              w += 1;
              if (w > 53) { w = 1; y += 1; }
            }
            return { y, w };
          })();
          const first = { y: yr, w: wk + 1 > 53 ? 1 : wk + 1 };
          if (n === 1) return `W${last.w}`;
          return `W${first.w} → W${last.w}`;
        };
        return (
          <div
            className="fixed inset-0 z-[100] bg-black/40 flex items-center justify-center p-4"
            onClick={() => !copying && setFanout(null)}
            data-testid="planning-week-fanout-overlay"
          >
            <div className="bg-white dark:bg-slate-800 rounded-lg shadow-2xl p-4 max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-sm font-semibold mb-1">Çoklu Hafta Kopyala</h3>
              <p className="text-xs text-slate-600 dark:text-slate-300 mb-3">
                Kaynak <strong>W{fanout.src.week}</strong> ({fanout.src.assigned} atama) sonraki kaç haftaya kopyalansın?
              </p>
              <div className="space-y-1.5">
                {presets.map((n) => (
                  <button
                    key={n}
                    onClick={() => performFanout({ src: fanout.src, count: n, overwrite: false })}
                    disabled={copying}
                    className="w-full text-left text-xs px-3 py-2 rounded border border-slate-200 dark:border-slate-700 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 hover:border-emerald-300 disabled:opacity-50"
                    data-testid={`planning-week-fanout-${n}`}
                  >
                    <div className="font-semibold flex items-center justify-between">
                      <span>Sonraki {n} hafta</span>
                      <span className="font-mono text-[10px] text-slate-500">{rangeLabel(n)}</span>
                    </div>
                    <div className="text-[10px] text-slate-500">Mevcut atamalar korunur, çakışmayanlar eklenir.</div>
                  </button>
                ))}
                <button
                  onClick={() => setFanout(null)}
                  disabled={copying}
                  className="w-full text-xs px-3 py-1.5 rounded text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700"
                >
                  İptal
                </button>
              </div>
              {copying && (
                <div className="mt-2 text-[11px] text-emerald-600 inline-flex items-center gap-1">
                  <Loader2 className="h-3 w-3 animate-spin" /> Kopyalanıyor…
                </div>
              )}
            </div>
          </div>
        );
      })()}
    </Popover>
  );
}

function heatColor(pct) {
  if (!pct) return 'bg-slate-200 dark:bg-slate-700';
  if (pct < 20) return 'bg-emerald-100 dark:bg-emerald-900/40';
  if (pct < 40) return 'bg-emerald-200 dark:bg-emerald-800';
  if (pct < 60) return 'bg-emerald-300 dark:bg-emerald-700';
  if (pct < 80) return 'bg-emerald-400 dark:bg-emerald-600';
  return 'bg-emerald-500 dark:bg-emerald-500 text-white';
}
